import { useMemo } from 'react';
import {
  MARGIN_BETWEEN_ROWS,
  TIMELINE_CHILD_INDENTATION,
  TIMELINE_LINK_BULLETPOINT_COLOR,
  TIMELINE_LINK_COLOR,
  TIMELINE_LINK_RIGHT_OFFSET,
  TIMELINE_LINK_WIDTH,
  TIMELINES_ITEM_ROW_HEIGHT,
} from '../consts';
import styled from 'styled-components';

const LIST_LINK_STEM_BOTTOM_OFFSET = TIMELINES_ITEM_ROW_HEIGHT / 2 + 1;
const LINK_THICKNESS = 2;

/** so that links are centrally aligned with labels */
const HORIZONTAL_LABEL_MARGIN_CORRECTION = 4;

const LabelsColumnListLinkContainer = styled.div`
  position: relative;
  pointer-events: none;
`;

const LabelsColumnListLinkBase = styled.div`
  position: absolute;
  z-index: 2;
`;

const LabelsColumnListLeafLink = styled(LabelsColumnListLinkBase)`
  width: ${TIMELINE_LINK_WIDTH}px;
  height: ${TIMELINES_ITEM_ROW_HEIGHT / 2}px;
  border-bottom: ${LINK_THICKNESS}px solid ${TIMELINE_LINK_COLOR};
  right: ${TIMELINE_LINK_RIGHT_OFFSET}px;
  &:after {
    position: absolute;
    z-index: 2;
    content: '';
    height: 6px;
    width: 6px;
    border-radius: 50%;
    background-color: ${TIMELINE_LINK_BULLETPOINT_COLOR};
    top: ${
      TIMELINES_ITEM_ROW_HEIGHT / 2 - HORIZONTAL_LABEL_MARGIN_CORRECTION
    }px;
    left: ${TIMELINE_LINK_WIDTH - 4}px;
  }
`;

const LabelsColumnListLinkStem = styled(LabelsColumnListLinkBase)`
  width: ${LINK_THICKNESS}px;
  background-color: ${TIMELINE_LINK_COLOR};
  bottom: ${LIST_LINK_STEM_BOTTOM_OFFSET}px;
  right: ${TIMELINE_LINK_WIDTH}px;
`;

const getTransitLinkStemStyle = (left: number) => ({
  top: 0,
  height: TIMELINES_ITEM_ROW_HEIGHT + MARGIN_BETWEEN_ROWS,
  width: LINK_THICKNESS,
  left,
});

const EXPANDER_CENTER_OFFSET = 16; // to align the link with the middle of the expander
const getTransitLinkIndent = (treeDepth: number) =>
  Math.max(0, treeDepth - 1) * TIMELINE_CHILD_INDENTATION +
  EXPANDER_CENTER_OFFSET;

const getLinkStemHeight = (isFirstChild: boolean) =>
  isFirstChild
    ? TIMELINES_ITEM_ROW_HEIGHT * 0.5
    : TIMELINES_ITEM_ROW_HEIGHT + MARGIN_BETWEEN_ROWS;

const Link = ({
  isFirstChild,
  transitLinks,
  treeDepth,
}: CollapsibleLinkArgs) => {
  const transitLinkStyles = transitLinks.map(linkDepth =>
    getTransitLinkStemStyle(getTransitLinkIndent(linkDepth))
  );

  return (
    <>
      <LabelsColumnListLeafLink />
      <LabelsColumnListLinkStem
        style={
          treeDepth > 0
            ? {
                height: `${getLinkStemHeight(isFirstChild)}px`,
              }
            : undefined
        }
      />
      {transitLinkStyles.map((style, index) => (
        <LabelsColumnListLinkStem key={index} style={style} />
      ))}
    </>
  );
};
type CollapsibleLinkArgs = {
  hasGrouping: boolean;
  treeDepth: number;
  isFirstChild: boolean;
  rowHeight: number;
  /**
   * A "transit link" is a line connecting a parent to its child that passes through the
   * current row (parent is above the current row, its child is below the current row).
   */
  transitLinks: number[];
};
const LabelsColumnListLink = (props: CollapsibleLinkArgs) => {
  const treeDepth = props.treeDepth;
  const indentation = TIMELINE_CHILD_INDENTATION * treeDepth;

  const dynamicStyle = useMemo(
    () => ({
      height: props.rowHeight,
      ...(treeDepth
        ? { width: `${indentation}px`, minWidth: `${indentation}px` }
        : {}),
    }),
    [props.rowHeight, indentation, treeDepth]
  );

  return (
    <LabelsColumnListLinkContainer style={dynamicStyle}>
      {props.hasGrouping && <Link {...props} />}
    </LabelsColumnListLinkContainer>
  );
};
export { LabelsColumnListLink };
